import axios from 'axios';
import { CONTRACTORS_HUB_API_BASE_URL } from '../config';
import { getAuthHeadersFromStorage } from '../utils/authHeaders';
import { attachAuthInterceptor } from '../utils/attachAuthInterceptor';

const client = axios.create({
  baseURL: CONTRACTORS_HUB_API_BASE_URL,
  withCredentials: true,
});

client.interceptors.request.use((config) => {
  // getAuthHeadersFromStorage() already prioritizes OAuth over API key and
  // returns the right single credential — don't also attach a (possibly
  // stale) X-API-Key on top, or an OAuth request can carry a leftover key
  // the backend rejects even though the Bearer token is valid.
  const authHeaders = getAuthHeadersFromStorage();
  if (Object.keys(authHeaders).length) {
    config.headers = { ...authHeaders, ...config.headers };
  }
  return config;
});
attachAuthInterceptor(client);

function formatApiDetail(detail) {
  if (detail == null || detail === '') return '';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object') {
          if (item.msg) return String(item.msg);
          if (item.message) return String(item.message);
        }
        try {
          return JSON.stringify(item);
        } catch (_err) {
          return 'Invalid request';
        }
      })
      .filter(Boolean)
      .join(', ');
  }
  if (typeof detail === 'object') {
    if (detail.message) return String(detail.message);
    try {
      return JSON.stringify(detail);
    } catch (_err) {
      return 'Request failed';
    }
  }
  return String(detail);
}

const extractError = (error) => {
  const payload = error?.response?.data;
  if (payload?.detail != null) {
    return formatApiDetail(payload.detail) || 'Request failed';
  }
  if (error?.code === 'ECONNABORTED') {
    return 'Request timed out. Check that the backend is running.';
  }
  return error?.message || 'Request failed';
};

const unwrap = async (promise) => {
  try {
    const response = await promise;
    return response.data;
  } catch (error) {
    throw new Error(extractError(error));
  }
};

export const listContractorAccounts = () =>
  unwrap(client.get('/api/v1/contractors-hub/contractor-accounts'));

export const getContractorAccount = (contractorAccountId) =>
  unwrap(client.get(`/api/v1/contractors-hub/contractor-accounts/${contractorAccountId}`));

export const createContractorAccount = ({ p21CustomerId, displayName }) =>
  unwrap(
    client.post('/api/v1/contractors-hub/contractor-accounts', {
      p21_customer_id: p21CustomerId,
      display_name: displayName,
    }),
  );

export const assignCatalogToAccount = (contractorAccountId, catalogMasterId) =>
  unwrap(
    client.post(`/api/v1/contractors-hub/contractor-accounts/${contractorAccountId}/catalog`, {
      catalog_master_id: catalogMasterId,
    }),
  );

export const connectCredentials = (
  contractorAccountId,
  {
    fsmPlatform = 'service_titan',
    environment = 'prod',
    owner,
    clientId,
    clientSecret,
    tenantId,
    appKey,
    authBaseUrl,
    apiBaseUrl,
  },
) =>
  unwrap(
    client.post(`/api/v1/contractors-hub/contractor-accounts/${contractorAccountId}/credentials`, {
      fsm_platform: fsmPlatform,
      environment,
      owner,
      client_id: clientId,
      client_secret: clientSecret,
      tenant_id: tenantId,
      app_key: appKey || '',
      auth_base_url: authBaseUrl || '',
      api_base_url: apiBaseUrl || '',
    }),
  );

export const revokeCredentials = (contractorAccountId, fsmPlatform = 'service_titan', environment = 'prod') =>
  unwrap(
    client.post(`/api/v1/contractors-hub/contractor-accounts/${contractorAccountId}/credentials/revoke`, null, {
      params: { fsm_platform: fsmPlatform, environment },
    }),
  );

export const testCredentialsConnection = (contractorAccountId, fsmPlatform = 'service_titan', environment = 'prod') =>
  unwrap(
    client.post(`/api/v1/contractors-hub/contractor-accounts/${contractorAccountId}/credentials/test`, null, {
      params: { fsm_platform: fsmPlatform, environment },
    }),
  );

// Both of a contractor's Credential Sets for a pipe (Test and Prod,
// whichever exist) -- never includes the encrypted secret.
export const listPipeConnections = (contractorAccountId, fsmPlatform = 'service_titan') =>
  unwrap(
    client.get(
      `/api/v1/contractors-hub/contractor-accounts/${contractorAccountId}/pipes/${fsmPlatform}/connections`,
    ),
  );

export const setDefaultEnvironment = (contractorAccountId, fsmPlatform, environment) =>
  unwrap(
    client.post(
      `/api/v1/contractors-hub/contractor-accounts/${contractorAccountId}/pipes/${fsmPlatform}/default-environment`,
      { environment },
    ),
  );

export const getPipeHistory = (contractorAccountId, fsmPlatform = 'service_titan', { page = 1, pageSize = 20 } = {}) =>
  unwrap(
    client.get(`/api/v1/contractors-hub/contractor-accounts/${contractorAccountId}/pipes/${fsmPlatform}/history`, {
      params: { page, page_size: pageSize },
    }),
  );

export const listCatalogs = () => unwrap(client.get('/api/v1/contractors-hub/catalogs'));

export const deleteCatalog = (masterId) =>
  unwrap(client.delete(`/api/v1/contractors-hub/catalogs/${masterId}`));

export const matchPartNumbers = (partNumbers, prices = {}) =>
  unwrap(
    client.post('/api/v1/contractors-hub/catalogs/match-part-numbers', {
      part_numbers: partNumbers,
      prices,
    }),
  );

// Reads from whichever search engine (Solr or OpenSearch) is configured for
// the tenant -- resolved server-side, see contractors_hub.py search-products.
export const searchCatalogProducts = ({ query, page = 1, pageSize = 20 }) =>
  unwrap(
    client.post('/api/v1/contractors-hub/catalogs/search-products', {
      query,
      page,
      page_size: pageSize,
    }),
  );

export const saveCatalog = ({ catalogKey, displayName, items }) =>
  unwrap(
    client.post('/api/v1/contractors-hub/catalogs', {
      catalog_key: catalogKey,
      display_name: displayName,
      items,
    }),
  );

export const getCatalog = (masterId) => unwrap(client.get(`/api/v1/contractors-hub/catalogs/${masterId}`));

export const addCatalogItem = (masterId, { sku, name, listPrice, customerPrice, description, imageUrl }) =>
  unwrap(
    client.post(`/api/v1/contractors-hub/catalogs/${masterId}/items`, {
      sku,
      name,
      list_price: listPrice,
      customer_price: customerPrice,
      description: description || name,
      image_url: imageUrl || '',
    }),
  );

export const removeCatalogItem = (masterId, sku) =>
  unwrap(client.delete(`/api/v1/contractors-hub/catalogs/${masterId}/items/${encodeURIComponent(sku)}`));

// environment: 'test' | 'prod' | undefined -- undefined resolves server-side
// to the contractor+pipe's default connection. prices: {sku: price} from
// OrderX's ERP pricing call (getOrderGenieErpPricesBulk), published as each
// item's customer price.
export const triggerPublish = (contractorAccountId, catalogVersionId, environment, prices = {}) =>
  unwrap(
    client.post(`/api/v1/contractors-hub/contractor-accounts/${contractorAccountId}/publish`, {
      catalog_version_id: catalogVersionId,
      environment: environment || null,
      prices,
    }),
  );

export const listPublishJobs = (contractorAccountId, { page = 1, pageSize = 20 } = {}) =>
  unwrap(
    client.get(`/api/v1/contractors-hub/contractor-accounts/${contractorAccountId}/publish-jobs`, {
      params: { page, page_size: pageSize },
    })
  );

// Polled while a publish job is pending/processing to drive a live progress
// bar (processed_count/total_count) -- see PublishActionSection.
export const getPublishJob = (jobId) => unwrap(client.get(`/api/v1/contractors-hub/publish-jobs/${jobId}`));

export const setContractorVendor = (contractorAccountId, vendorId) =>
  unwrap(
    client.put(`/api/v1/contractors-hub/contractor-accounts/${contractorAccountId}/vendor`, {
      vendor_id: vendorId,
    }),
  );

export const setCatalogPublishSettings = (
  masterId,
  { categories, active, taxable, account, costOfSaleAccount },
  fsmPlatform = 'service_titan',
) =>
  unwrap(
    client.put(`/api/v1/contractors-hub/catalogs/${masterId}/publish-settings/${fsmPlatform}`, {
      categories,
      active,
      taxable,
      account,
      cost_of_sale_account: costOfSaleAccount,
    }),
  );

// A mapping is { [targetPath]: { mode: 'field', source: '<product field path>' }
//                              | { mode: 'value', value: <any JSON literal> } }
// -- fully data-driven, see backend/services/contractors_hub/field_mappings.py.
export const getFieldMapping = async (fsmPlatform = 'service_titan') => {
  const data = await unwrap(client.get(`/api/v1/contractors-hub/pipes/${fsmPlatform}/field-mapping`));
  return data.mapping;
};

export const setFieldMapping = async (mapping, fsmPlatform = 'service_titan') => {
  const data = await unwrap(client.put(`/api/v1/contractors-hub/pipes/${fsmPlatform}/field-mapping`, { mapping }));
  return data.mapping;
};

// Suggestions for the Mapping screen's "+ Add field" pickers -- not the
// full set of what CAN be mapped (any dot-path is allowed), just what's
// known/confirmed so far.
export const getFieldCatalog = (fsmPlatform = 'service_titan') =>
  unwrap(client.get(`/api/v1/contractors-hub/pipes/${fsmPlatform}/field-catalog`));

// Per-contractor, per-environment Constants/mapping override -- falls back
// to the tenant-wide mapping above when this connection has never saved
// its own (backend/services/contractors_hub/field_mappings.py).
export const getConnectionFieldMapping = async (contractorAccountId, fsmPlatform, environment) => {
  const data = await unwrap(
    client.get(
      `/api/v1/contractors-hub/contractor-accounts/${contractorAccountId}/pipes/${fsmPlatform}/field-mapping`,
      { params: { environment } },
    ),
  );
  return data.mapping;
};

export const setConnectionFieldMapping = async (contractorAccountId, fsmPlatform, environment, mapping) => {
  const data = await unwrap(
    client.put(
      `/api/v1/contractors-hub/contractor-accounts/${contractorAccountId}/pipes/${fsmPlatform}/field-mapping`,
      { mapping },
      { params: { environment } },
    ),
  );
  return data.mapping;
};
